import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL, EMAIL_API_URL } from "../../config";
import { toast } from "react-toastify";
import { setInboxUnreadCount, selectLastInboxEvent } from "../../store/auth/authSlice";
import { authHeaders } from "../utils/session";
import WidgetBodyEditor from "../email-widgets/WidgetBodyEditor";
import { useActiveEmailWidgets } from "../hooks/useEmailWidgets";
const jsonHeaders = () => ({
  ...authHeaders(),
});

const getStoredJson = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {}; }
};

const sanitizeForCompose = (html) => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("style, script, link[rel='stylesheet']").forEach((el) => el.remove());
    return doc.body.innerHTML || "";
  } catch {
    return html;
  }
};

const AVATAR_COLORS = [
  "#0078d4", "#107c10", "#c239b3", "#8764b8",
  "#038387", "#e97525", "#744da9", "#018574",
  "#00b7c3", "#d13438", "#498205", "#ca5010",
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Subscription-state filter chips — same set/values as the Bulk Email composer, so the
// inbox customer list can be narrowed the same way.
// Exactly the subscription Kanban columns (manage-order) — same set, order, labels and
// colours — so the inbox stays in sync with that board.
const FILTER_CHIPS = [
  { value: "",                   label: "All",                color: "#302b63", bg: "#302b6315", border: "#302b6340" },
  { value: "trial",              label: "Active Free Trial",  color: "#0369a1", bg: "#e0f2fe", border: "#bae6fd" },
  { value: "payment_pending",    label: "Pending Payment",    color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  { value: "paid_active",        label: "Active Paid",        color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  { value: "expiring_soon",      label: "Expiring Soon",      color: "#ea580c", bg: "#fff7ed", border: "#fed7aa" },
  { value: "paid_expired",       label: "Paid Expired",       color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  { value: "free_trial_expired", label: "Free Trial Expired", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  { value: "payment_rejected",   label: "Payment Rejected",   color: "#b91c1c", bg: "#fff1f2", border: "#fecdd3" },
];

// Maps a chip value to get-customer-list-v3 query params (mirrors BulkEmailCompose).
const buildFilterParams = (filter) => {
  if (!filter) return {};
  if (filter === "trial") return { customer_type: "trial" };
  return { customer_status: filter };
};

export default function EmailInbox() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [thread, setThread] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const CUSTOMER_LIMIT = 10;
  const sentinelRef = useRef(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Subscription-state filter (chip). Ref keeps the latest value for paging/closures.
  const [customerFilter, setCustomerFilter] = useState("");
  const customerFilterRef = useRef("");
  customerFilterRef.current = customerFilter;
  const [newEmailCount, setNewEmailCount] = useState(0);
  const [expandedMsgs, setExpandedMsgs] = useState(new Set());
  const leadsRef = useRef([]);
  const threadEndRef = useRef(null);

  const internalUserId = Number(localStorage.getItem("trade_user_id")) || 0;

  // Compose / reply state
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [composeCc, setComposeCc] = useState("");
  const [composeBcc, setComposeBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [bodyMode, setBodyMode] = useState("compose"); // "compose" | "html" | "preview"
  const [bodyHtml, setBodyHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  // Subscription inbox conversation tracking
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Email template state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Email widgets (2026-10-27): a template with a company design is edited as plain text.
  const { data: activeWidgets = [] } = useActiveEmailWidgets();
  const templateWidget = selectedTemplate?.widget_id
    ? activeWidgets.find((w) => w.id === selectedTemplate.widget_id) || null
    : null;

  // Keep ref in sync so polling closure always sees latest leads
  useEffect(() => { leadsRef.current = leads; }, [leads]);

  // Scroll to latest message whenever thread changes
  useEffect(() => {
    if (thread.length > 0 && threadEndRef.current) {
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }, [thread]);

  // Sync inbox unread count to Redux so sidebar can show badge
  const totalUnread = leads.reduce((sum, l) => sum + (l.unread_count || 0), 0);
  useEffect(() => {
    dispatch(setInboxUnreadCount(totalUnread));
  }, [totalUnread, dispatch]);

  const fetchTemplates = useCallback(async () => {
    try {
      /* Subscription Inbox writes to paying customers, so only customer
         templates belong here — the same way the lead screens ask for
         context_type=lead. */
      const res = await axios.get(`${API_BASE_URL}/get-email-template-list`, {
        params: { context_type: "customer" },
        headers: jsonHeaders(),
      });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setEmailTemplates(list);
    } catch {
      // non-critical — templates just won't show
    }
  }, []);

  const fetchLeads = useCallback(async (page = 1, append = false, silent = false, filter = customerFilterRef.current) => {
    if (append) setIsFetchingMore(true);
    else if (!silent) setLoadingLeads(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/subscription/get-customer-list-v3`, {
        params: { page, limit: CUSTOMER_LIMIT, ...buildFilterParams(filter) },
        headers: jsonHeaders(),
      });
      const raw = res.data?.data ?? res.data;
      const list = Array.isArray(raw) ? raw : [];
      const total = res.data?.total || res.data?.pagination?.total || list.length;
      setHasMore(page * CUSTOMER_LIMIT < total);
      const mapped = list.map((item) => ({
        ...item,
        id: item.user_id || item.id,
        name: item.customer_name || item.name || "",
        email: item.customer_email || item.email || "",
        unread_count: 0,
        last_email_at: item.last_email_at || null,
        last_subject: item.last_subject || null,
      }));
      if (append) {
        setLeads((prev) => {
          const seen = new Set(prev.map((l) => l.id));
          return [...prev, ...mapped.filter((l) => !seen.has(l.id))];
        });
      } else {
        setLeads(mapped);
      }
      // Merge unread counts from the inbox-specific endpoint in the background
      mergeUnreadCountsRef.current();
    } catch {
      if (!append) setLeads([]);
    } finally {
      if (append) setIsFetchingMore(false);
      else if (!silent) setLoadingLeads(false);
    }
  }, []);


  // Fetch next page (append) when customerPage increments; page 1 is always a fresh load
  useEffect(() => {
    fetchLeads(customerPage, customerPage > 1);
  }, [customerPage, fetchLeads]);

  // Auto-select user when navigated from another page (e.g. subscription list ?userId=).
  // The customer may not be on the first loaded page, so fall back to the name/email the
  // link carries and build a minimal lead — the render keeps the selected row in the list
  // (see displayLeads below) so it is highlighted + scrolled into view either way.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    const targetId = searchParams.get("userId");
    if (!targetId || autoSelectedRef.current) return;
    const existing = leads.find((l) => String(l.id) === String(targetId));
    if (existing) {
      autoSelectedRef.current = true;
      handleSelectLead(existing);
      return;
    }
    const name = searchParams.get("name");
    const email = searchParams.get("email");
    if (name || email) {
      autoSelectedRef.current = true;
      handleSelectLead({
        id: Number(targetId) || targetId,
        name: name || "",
        email: email || "",
        unread_count: 0,
        last_email_at: null,
        last_subject: null,
      });
    }
    // else: no hint on the link — wait for more pages to load, this effect re-runs on `leads`.
  }, [leads, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep refs in sync so the observer callback always reads fresh values without re-subscribing
  const hasMoreRef = useRef(hasMore);
  const isFetchingMoreRef = useRef(isFetchingMore);
  const loadingLeadsRef = useRef(loadingLeads);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { isFetchingMoreRef.current = isFetchingMore; }, [isFetchingMore]);
  useEffect(() => { loadingLeadsRef.current = loadingLeads; }, [loadingLeads]);

  // IntersectionObserver created once — uses refs to avoid infinite re-subscription
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !isFetchingMoreRef.current && !loadingLeadsRef.current) {
          setCustomerPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: load templates
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Refs so polling closures always see latest values without restarting the effect
  const selectedLeadRef = useRef(null);
  const activeConvIdRef = useRef(null);
  const threadLengthRef = useRef(0);
  selectedLeadRef.current = selectedLead;
  activeConvIdRef.current = activeConversationId;
  threadLengthRef.current = thread.length;

  // Fetch per-customer unread counts from inbox endpoint and merge into leads list
  const mergeUnreadCounts = useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(`${API_BASE_URL}/subscription-inbox/list`, {
        params: { page: 1, limit: 200, internal_user_id: internalUserId },
        headers: jsonHeaders(),
      });
      const raw = res.data?.data ?? res.data;
      const inboxList = Array.isArray(raw) ? raw : [];
      if (inboxList.length === 0) return;
      // Build { userId -> { unread_count, last_email_at, last_subject } } map
      const unreadMap = {};
      inboxList.forEach((item) => {
        const uid = item.user_id || item.id || item.customer_id;
        if (uid) {
          unreadMap[String(uid)] = {
            unread_count: item.unread_count || item.unread_email_count || 0,
            last_email_at: item.last_email_at || item.last_reply_at || null,
            last_subject: item.last_subject || item.subject || null,
          };
        }
      });
      setLeads((prev) =>
        prev.map((l) => {
          const info = unreadMap[String(l.id)];
          if (!info) return l;
          return {
            ...l,
            unread_count: info.unread_count,
            last_email_at: info.last_email_at || l.last_email_at,
            last_subject: info.last_subject || l.last_subject,
          };
        })
      );
    } catch {}
  }, []);

  // Stable ref so fetchLeads can call it without creating a circular dependency
  const mergeUnreadCountsRef = useRef(mergeUnreadCounts);
  mergeUnreadCountsRef.current = mergeUnreadCounts;

  // Lightweight thread refresh — reads already-ingested rows, detects new inbound messages
  const refreshThread = useCallback(async () => {
    const lead = selectedLeadRef.current;
    const convId = activeConvIdRef.current;
    if (!lead || !convId) return;
    try {
      const internalUserIdRefresh = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(
        `${API_BASE_URL}/subscription-inbox/thread/${lead.id}`,
        { params: { conversation_id: convId, internal_user_id: internalUserIdRefresh }, headers: jsonHeaders() }
      );
      const arr = Array.isArray(res.data?.data) ? res.data.data : [];
      const sorted = [...arr].sort((a, b) =>
        (a.sent_at ? new Date(a.sent_at).getTime() : 0) -
        (b.sent_at ? new Date(b.sent_at).getTime() : 0)
      );
      const prevLen = threadLengthRef.current;
      if (sorted.length === prevLen) return; // nothing new — skip re-render
      const newIds = sorted.slice(prevLen).map((m) => m.id).filter(Boolean);
      if (newIds.length > 0) {
        setExpandedMsgs((prev) => new Set([...prev, ...newIds]));
      }
      setThread(sorted);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redux SSE fan-out: refetch open thread when a matching inbound subscription event arrives
  const lastInboxEvent = useSelector(selectLastInboxEvent);
  useEffect(() => {
    if (!lastInboxEvent || lastInboxEvent.type !== "inbound" || lastInboxEvent.inbox !== "subscription") return;
    const lead = selectedLeadRef.current;
    if (!lead || String(lastInboxEvent.customer_id) !== String(lead.id)) return;
    refreshThread();
    mergeUnreadCountsRef.current();
  }, [lastInboxEvent]); // eslint-disable-line

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([refreshThread(), fetchLeads(1, false, true), mergeUnreadCounts()]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelectLead = async (lead) => {
    setSelectedLead(lead);
    setNewEmailCount(0);
    setLoadingThread(true);
    setActiveConversationId(null);
    try {
      // Fetch conversations from subscription-inbox to get conversation_id for replies
      let convId = null;
      try {
        const internalUserIdConv = Number(localStorage.getItem("trade_user_id"));
        const convRes = await axios.get(
          `${API_BASE_URL}/subscription-inbox/conversations/${lead.id}`,
          { params: { internal_user_id: internalUserIdConv }, headers: jsonHeaders() }
        );
        const convs = Array.isArray(convRes.data?.data) ? convRes.data.data : [];
        if (convs.length > 0) convId = convs[0].conversation_id;
      } catch {}
      setActiveConversationId(convId);

      // Fetch thread from subscription-inbox
      let arr = [];
      if (convId) {
        const internalUserIdThread = Number(localStorage.getItem("trade_user_id"));
        const res = await axios.get(
          `${API_BASE_URL}/subscription-inbox/thread/${lead.id}`,
          { params: { conversation_id: convId, internal_user_id: internalUserIdThread }, headers: jsonHeaders() }
        );
        arr = Array.isArray(res.data?.data) ? res.data.data : [];
      }

      const sorted = [...arr].sort((a, b) => {
        const ta = a.sent_at ? new Date(a.sent_at).getTime() : 0;
        const tb = b.sent_at ? new Date(b.sent_at).getTime() : 0;
        return ta - tb;
      });
      setThread(sorted);
      if (sorted.length > 0) {
        setExpandedMsgs(new Set([sorted[sorted.length - 1].id]));
      } else {
        setExpandedMsgs(new Set());
      }
      // Mark as read locally and on backend, then re-sync unread counts
      setLeads((prev) =>
        prev.map((l) => (l.id === lead.id ? { ...l, unread_count: 0 } : l))
      );
      const internalUserIdMark = Number(localStorage.getItem("trade_user_id"));
      axios.patch(
        `${API_BASE_URL}/subscription-inbox/mark-read/${lead.id}`,
        { internal_user_id: internalUserIdMark },
        { headers: jsonHeaders() }
      ).then(() => mergeUnreadCounts()).catch(() => {});
    } catch {
      toast.error("Failed to load conversation.");
      setThread([]);
    } finally {
      setLoadingThread(false);
    }
  };


  const handleSelectLeadWithCompose = (lead) => {
    handleSelectLead(lead);
    closeCompose();
  };

  const closeCompose = () => {
    setShowCompose(false);
    setComposeSubject("");
    setComposeAttachments([]);
    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setBodyHtml("");
    setBodyMode("compose");
    setShowCc(false);
    setShowBcc(false);
    setComposeCc("");
    setComposeBcc("");
  };

  const openReply = () => {
    const lastSubject = thread.length > 0 ? (thread[thread.length - 1].subject || "") : "";
    const subject = lastSubject ? (lastSubject.startsWith("Re:") ? lastSubject : `Re: ${lastSubject}`) : "";
    setComposeSubject(subject);
    setComposeAttachments([]);
    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setBodyHtml("");
    setBodyMode("compose");
    setShowCc(false);
    setShowBcc(false);
    setComposeCc("");
    setComposeBcc("");
    setShowCompose(true);
  };

  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  // When switching back to compose mode, inject sanitized HTML (strips <style>/<script>)
  useEffect(() => {
    if (bodyMode === "compose" && editorRef.current) {
      editorRef.current.innerHTML = sanitizeForCompose(bodyHtml);
    }
  }, [bodyMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPreview = async (userId, subject, body) => {
    if (!userId) return;
    setPreviewLoading(true);
    try {
      const res = await axios.post(
        `${EMAIL_API_URL}/api/bulk-email/preview`,
        { subject, body, user_id: userId },
        { headers: jsonHeaders() }
      );
      const result = res.data?.data ?? res.data;
      if (result) {
        if (result.subject) setComposeSubject(result.subject);
        setBodyHtml(result.body || body || "");
      }
    } catch {
      setBodyHtml(body || "");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedLead) return;
    if (!selectedTemplateId && !bodyHtml.replace(/<[^>]*>/g, "").trim()) return;
    setSending(true);
    try {
      const fromEmail = getStoredJson("trade_user_data")?.email || "";
      const fd = new FormData();
      fd.append("from", fromEmail);
      fd.append("to", selectedLead.email);
      fd.append("customer_id", String(selectedLead.id));
      fd.append("internal_user_id", String(Number(localStorage.getItem("trade_user_id")) || ""));
      fd.append("conversation_id", activeConversationId || "");
      if (composeCc) fd.append("cc", composeCc);
      if (composeBcc) fd.append("bcc", composeBcc);
      composeAttachments.forEach((f) => fd.append("attachments", f));

      if (selectedTemplateId) {
        fd.append("template_id", String(selectedTemplateId));
        // The message as edited — the server puts it into the template's company design.
        if (templateWidget) fd.append("body", bodyHtml);
        await axios.post(`${API_BASE_URL}/subscription-inbox/send-using-template`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
      } else {
        fd.append("subject", composeSubject);
        fd.append("body", bodyHtml); // bodyHtml state
        await axios.post(`${API_BASE_URL}/subscription-inbox/send`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
      }

      toast.success("Email sent successfully");
      closeCompose();
      setTimeout(() => handleSelectLead(selectedLead), 1500);
    } catch {
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const filteredLeads = leads
    .filter((l) => {
      const q = searchQuery.toLowerCase();
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.last_subject || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      // Unread rows always on top; within each group sort by most recent email
      if ((b.unread_count || 0) !== (a.unread_count || 0)) {
        return (b.unread_count || 0) - (a.unread_count || 0);
      }
      const ta = a.last_email_at ? new Date(a.last_email_at).getTime() : 0;
      const tb = b.last_email_at ? new Date(b.last_email_at).getTime() : 0;
      return tb - ta;
    });

  // Always keep the selected customer in the rendered list — when it was opened via a
  // ?userId link it may not be on a loaded page yet. Pinning it at the top guarantees the
  // row is present (so it highlights) and can be scrolled into view.
  const displayLeads =
    selectedLead && !filteredLeads.some((l) => String(l.id) === String(selectedLead.id))
      ? [selectedLead, ...filteredLeads]
      : filteredLeads;

  // Scroll the highlighted row into view when the selection changes.
  const selectedRowRef = useRef(null);
  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedLead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const isToday =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const toggleMsg = (id) => {
    setExpandedMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };



  // ─── INBOX ────────────────────────────────────────────────────────────────

  return (
    <div className="main-content">
      <div className="page-content" style={{ padding: "70px 0 0" }}>
        <div
          style={{
            display: "flex",
            height: "calc(100vh - 130px)",
            background: "#f8fafc",
            overflow: "hidden",
          }}
        >
          {/* ── LEFT PANEL ── */}
          <div
            style={{
              width: 320,
              minWidth: 320,
              background: "#fff",
              borderRight: "1px solid #e8edf3",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Left Header */}
            <div
              style={{
                padding: "16px 16px 12px",
                borderBottom: "1px solid #f0f4f8",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "linear-gradient(135deg, #0078d4, #00bcf2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className="bx bx-envelope"
                      style={{ fontSize: 16, color: "#fff" }}
                    />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2332" }}>
                        Inbox
                      </div>
                      {newEmailCount > 0 && (
                        <span style={{
                          background: "#0078d4", color: "#fff",
                          borderRadius: 10, padding: "1px 7px",
                          fontSize: 11, fontWeight: 700,
                        }}>{newEmailCount} new</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {totalUnread > 0
                        ? `${totalUnread} unread`
                        : `${leads.length} conversations`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh inbox"
                  style={{
                    background: "linear-gradient(135deg, #0078d4, #00bcf2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: refreshing ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    opacity: refreshing ? 0.75 : 1,
                  }}
                >
                  {refreshing ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      style={{ width: 12, height: 12 }}
                    />
                  ) : (
                    <i className="bx bx-refresh" style={{ fontSize: 14 }} />
                  )}
                  Refresh
                </button>
              </div>

              {/* Search */}
              <div style={{ position: "relative", marginTop: 8 }}>
                <i
                  className="bx bx-search"
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: 15,
                  }}
                />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px 8px 32px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "#374151",
                    background: "#f8fafc",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Subscription-state filter chips (same set as Bulk Email) */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                {FILTER_CHIPS.map((opt) => {
                  const active = customerFilter === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => {
                        const f = active ? "" : opt.value;
                        customerFilterRef.current = f;
                        setCustomerFilter(f);
                        setHasMore(true);
                        setCustomerPage(1);
                        fetchLeads(1, false, false, f);
                      }}
                      style={{
                        padding: "3px 9px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        userSelect: "none",
                        color: active ? "#fff" : opt.color,
                        background: active ? opt.color : opt.bg,
                        border: `1px solid ${active ? opt.color : opt.border}`,
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Lead List */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {loadingLeads ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "32px 0",
                  }}
                >
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ color: "#0078d4" }}
                  />
                </div>
              ) : displayLeads.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 16px",
                    color: "#94a3b8",
                    fontSize: 13,
                  }}
                >
                  {searchQuery ? "No results found." : "No conversations yet."}
                </div>
              ) : (
                displayLeads.map((lead, idx) => {
                  const isSelected = String(selectedLead?.id) === String(lead.id);
                  const isUnread = lead.unread_count > 0;
                  const initial = (lead.name || lead.email || "?")[0].toUpperCase();

                  return (
                    <div
                      key={`${lead.id}-${idx}`}
                      ref={isSelected ? selectedRowRef : undefined}
                      onClick={() => handleSelectLeadWithCompose(lead)}
                      style={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        borderLeft: isSelected
                          ? "3px solid #0078d4"
                          : "3px solid transparent",
                        background: isSelected ? "#e8f0fe" : "#fff",
                        borderBottom: "1px solid #f0f4f8",
                        transition: "background 0.15s",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                        }}
                      >
                        {/* Avatar */}
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: "50%",
                            background: getAvatarColor(lead.name || lead.email),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 15,
                            flexShrink: 0,
                          }}
                        >
                          {initial}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: isUnread ? 700 : 500,
                                color: "#1a2332",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 160,
                              }}
                            >
                              {lead.name || lead.email}
                            </span>
                            <span
                              style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}
                            >
                              {formatDate(lead.last_email_at)}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              color: isUnread ? "#0078d4" : "#64748b",
                              fontWeight: isUnread ? 600 : 400,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginBottom: 3,
                            }}
                          >
                            {lead.last_subject || "(No subject)"}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                color: "#94a3b8",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {lead.email}
                            </span>
                            {isUnread && (
                              <span
                                style={{
                                  background: "linear-gradient(135deg, #0078d4, #00bcf2)",
                                  color: "#fff",
                                  borderRadius: 10,
                                  padding: "1px 7px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  marginLeft: 6,
                                }}
                              >
                                {lead.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {/* Infinite scroll sentinel — must be inside the scrollable div */}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {isFetchingMore && (
                <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
                  <span className="spinner-border spinner-border-sm" style={{ color: "#0078d4", width: 18, height: 18 }} />
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {!selectedLead ? (
              // Empty state
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94a3b8",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 18,
                    background: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="bx bx-envelope-open"
                    style={{ fontSize: 34, color: "#cbd5e1" }}
                  />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{ fontSize: 15, fontWeight: 600, color: "#64748b" }}
                  >
                    Select a conversation
                  </div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
                    Choose a customer from the list to view their email thread.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Thread Header */}
                <div style={{
                  padding: "14px 24px",
                  background: "#fff",
                  borderBottom: "1px solid #e1dfdd",
                  flexShrink: 0,
                }}>
                  {/* Subject line */}
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#1a2332", marginBottom: 10 }}>
                    {thread[0]?.subject || "(No subject)"}
                  </div>
                  {/* Contact row */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                        background: getAvatarColor(selectedLead.name || selectedLead.email),
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontWeight: 700, fontSize: 15,
                      }}>
                        {(selectedLead.name || selectedLead.email || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a2332" }}>
                          {selectedLead.name || selectedLead.email}
                        </div>
                        <div style={{ fontSize: 11, color: "#8a8886" }}>{selectedLead.email}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {thread.length > 0 && (
                        <span style={{
                          background: "#e8f0fe", color: "#0078d4",
                          borderRadius: 12, padding: "3px 12px", fontSize: 12, fontWeight: 600,
                        }}>
                          {thread.length} {thread.length === 1 ? "message" : "messages"}
                        </span>
                      )}
                      <button
                        onClick={showCompose ? closeCompose : openReply}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "6px 16px", borderRadius: 8, border: "none",
                          background: showCompose ? "#e8f0fe" : "linear-gradient(135deg,#0078d4,#00bcf2)",
                          color: showCompose ? "#0078d4" : "#fff",
                          fontSize: 13, fontWeight: 600, cursor: "pointer",
                          boxShadow: showCompose ? "none" : "0 2px 8px rgba(0,120,212,0.25)",
                        }}
                      >
                        <i className={`bx ${showCompose ? "bx-x" : "bx-edit"}`} style={{ fontSize: 15 }} />
                        {showCompose ? "Cancel" : "Compose"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Thread Messages — Outlook style */}
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    background: "#f3f2f1",
                    padding: "16px 24px 24px",
                  }}
                >
                  {loadingThread ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                      <div className="spinner-border" role="status" style={{ color: "#0078d4", width: 32, height: 32 }} />
                    </div>
                  ) : thread.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, marginTop: 40 }}>
                      No messages in this thread yet.
                    </div>
                  ) : (
                    thread.map((msg) => {
                      const isOutgoing = msg.direction === "outgoing";
                      const isFailed = msg.status === "failed";
                      const senderLabel = isOutgoing ? "Me" : (msg.from_email || "Unknown");
                      const senderInitial = (isOutgoing ? "M" : (msg.from_email || "?")[0]).toUpperCase();
                      const isHtml = msg.body && (msg.body.trim().startsWith("<") || msg.body.includes("<html"));
                      const isExpanded = expandedMsgs.has(msg.id);

                      // Plain-text preview for collapsed state
                      const bodyPreview = msg.body
                        ? msg.body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120)
                        : "";

                      return (
                        <div
                          key={msg.id}
                          style={{
                            background: isFailed ? "#fef2f2" : "#fff",
                            border: "1px solid #e1dfdd",
                            borderLeft: isFailed ? "3px solid #dc2626" : isOutgoing ? "3px solid #0078d4" : "3px solid #e1dfdd",
                            borderRadius: 4,
                            marginBottom: 10,
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                          }}
                        >
                          {/* Email card header — always visible, click to toggle */}
                          <div
                            onClick={() => toggleMsg(msg.id)}
                            style={{
                              padding: "12px 16px",
                              borderBottom: isExpanded ? "1px solid #f3f2f1" : "none",
                              background: isOutgoing ? "#f0f7ff" : "#fafafa",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 10,
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                          >
                            {/* Avatar */}
                            <div style={{
                              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                              background: getAvatarColor(isOutgoing ? "Me" : (msg.from_email || "?")),
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: "#fff", fontWeight: 700, fontSize: 14,
                            }}>
                              {senderInitial}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Row 1: sender + time + chevron */}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: "#1a2332" }}>{senderLabel}</span>
                                  {isFailed && (
                                    <span title={msg.error || "Delivery failed"}
                                      style={{ background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 12, fontSize: 11, fontWeight: 700, padding: "1px 8px", cursor: "default" }}>
                                      ⚠ Failed
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                  {isOutgoing && !isFailed && (
                                    <span style={{
                                      fontSize: 10, fontWeight: 600, padding: "2px 7px",
                                      borderRadius: 10, background: "#e3f2fd", color: "#0078d4",
                                    }}>Sent</span>
                                  )}
                                  <span style={{ fontSize: 11, color: "#8a8886" }}>{formatDateTime(msg.sent_at)}</span>
                                  <i
                                    className={`bx bx-chevron-${isExpanded ? "up" : "down"}`}
                                    style={{ fontSize: 16, color: "#8a8886", transition: "transform 0.2s" }}
                                  />
                                </div>
                              </div>
                              {/* Collapsed: show preview; Expanded: show To line */}
                              {isExpanded ? (
                                <div style={{ fontSize: 11, color: "#8a8886" }}>
                                  To: <span style={{ color: "#323130" }}>{msg.to_email}</span>
                                </div>
                              ) : (
                                <div style={{
                                  fontSize: 12, color: "#8a8886",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                  {bodyPreview || "(No content)"}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Expanded content */}
                          {isExpanded && (
                            <>
                              {/* Subject */}
                              {msg.subject && (
                                <div style={{ padding: "10px 16px 0", fontWeight: 700, fontSize: 14, color: "#323130" }}>
                                  {msg.subject}
                                </div>
                              )}

                              {/* Body */}
                              <div style={{ padding: "10px 16px 14px" }}>
                                <iframe
                                  srcDoc={(() => {
                                    const reset = `<style>body{margin:0!important;padding:0!important}</style>`;
                                    return isHtml
                                      ? msg.body.replace(/<head>/i, `<head>${reset}`).replace(/^(?![\s\S]*<head>)/, `<head>${reset}</head>`)
                                      : `<html><head>${reset}</head><body style="font-family:Arial,sans-serif;font-size:14px;color:#323130;line-height:1.6;margin:0;padding:0;white-space:pre-wrap;">${msg.body}</body></html>`;
                                  })()}
                                  style={{ width: "100%", border: "none", display: "block" }}
                                  scrolling="no"
                                  onLoad={(e) => {
                                    const doc = e.target.contentDocument;
                                    if (doc) e.target.style.height = doc.body.scrollHeight + "px";
                                  }}
                                  title="email-body"
                                  sandbox="allow-same-origin"
                                />
                                {isFailed && msg.error && (
                                  <div style={{ marginTop: 8, padding: "6px 10px", background: "#fee2e2", borderRadius: 6, fontSize: 12, color: "#b91c1c", display: "flex", alignItems: "center", gap: 6 }}>
                                    ⚠ Delivery failed: {msg.error}
                                  </div>
                                )}
                              </div>

                              {/* Attachments */}
                              {(() => {
                                let atts = msg.attachments;
                                if (!atts) return null;
                                if (typeof atts === "string") { try { atts = JSON.parse(atts); } catch { atts = []; } }
                                if (!Array.isArray(atts) || atts.length === 0) return null;
                                return (
                                  <div style={{ padding: "8px 16px 12px", borderTop: "1px solid #f3f2f1", display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {atts.map((att, i) => {
                                      const name = att.name || att.filename || att.originalname || `Attachment ${i + 1}`;
                                      const url = att.url || att.path || att.file_url || null;
                                      const ext = name.split(".").pop().toLowerCase();
                                      const isImage = ["jpg","jpeg","png","gif","webp","bmp"].includes(ext);
                                      const iconColor = ext === "pdf" ? "#ef4444" : ["doc","docx"].includes(ext) ? "#2563eb" : ["xls","xlsx"].includes(ext) ? "#16a34a" : "#64748b";
                                      return (
                                        <a key={i} href={url || "#"} target={url ? "_blank" : undefined} rel="noopener noreferrer"
                                          style={{
                                            display: "inline-flex", alignItems: "center", gap: 6,
                                            padding: "5px 12px", borderRadius: 8,
                                            background: "#f8fafc", border: "1px solid #e2e8f0",
                                            textDecoration: "none", color: "#374151", fontSize: 12, fontWeight: 500,
                                            cursor: url ? "pointer" : "default",
                                          }}
                                        >
                                          {isImage
                                            ? <i className="bx bx-image" style={{ fontSize: 15, color: "#0078d4" }} />
                                            : <i className="bx bx-file" style={{ fontSize: 15, color: iconColor }} />
                                          }
                                          {name}
                                          {url && <i className="bx bx-download" style={{ fontSize: 13, color: "#94a3b8" }} />}
                                        </a>
                                      );
                                    })}
                                  </div>
                                );
                              })()}

                              {/* Reply button — inbound emails only */}
                              {!isOutgoing && (
                                <div style={{ padding: "8px 16px 10px", borderTop: "1px solid #f3f2f1" }}>
                                  <button
                                    onClick={() => {
                                      const subj = msg.subject || "";
                                      setComposeSubject(subj.startsWith("Re:") ? subj : subj ? `Re: ${subj}` : "");
                                      setSelectedTemplateId("");
                                      setSelectedTemplate(null);
                                      setBodyHtml("");
                                      setBodyMode("compose");
                                      setShowCc(false);
                                      setShowBcc(false);
                                      setComposeCc("");
                                      setComposeBcc("");
                                      setComposeAttachments([]);
                                      setShowCompose(true);
                                    }}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 5,
                                      padding: "5px 14px", borderRadius: 6, border: "1px solid #e2e8f0",
                                      background: "#f8fafc", color: "#0078d4",
                                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    }}
                                  >
                                    <i className="bx bx-reply" style={{ fontSize: 14 }} /> Reply
                                  </button>
                                </div>
                              )}

                              {/* Footer: template / delivery info */}
                              {(msg.template_used || msg.delivery_status) && (
                                <div style={{
                                  padding: "6px 16px", borderTop: "1px solid #f3f2f1",
                                  display: "flex", alignItems: "center", gap: 12, background: "#faf9f8",
                                }}>
                                  {msg.template_used && (
                                    <span style={{ fontSize: 11, color: "#8a8886" }}>
                                      Template: <span style={{ color: "#0078d4", fontWeight: 500 }}>{msg.template_used}</span>
                                    </span>
                                  )}
                                  {msg.delivery_status && (
                                    <span style={{
                                      fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 10,
                                      background: msg.delivery_status === "sent" ? "#e8f5e9" : "#fff3e0",
                                      color: msg.delivery_status === "sent" ? "#2e7d32" : "#e65100",
                                    }}>
                                      {msg.delivery_status}
                                    </span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div ref={threadEndRef} />
                </div>

              </>
            )}
          </div>
        </div>
      </div>

      {/* ── COMPOSE MAIL MODAL ── */}
      {showCompose && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1050,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.35)",
        }} onClick={(e) => { if (e.target === e.currentTarget) closeCompose(); }}>
          <div style={{
            width: 660, maxWidth: "95vw", background: "#fff",
            borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{
              padding: "14px 20px",
              background: "linear-gradient(135deg,#5b48e8,#7c3aed)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexShrink: 0,
            }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                <i className="bx bx-envelope" style={{ fontSize: 18 }} /> Compose Mail
              </span>
              <button onClick={closeCompose} style={{
                background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
                cursor: "pointer", width: 28, height: 28, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
              }}>×</button>
            </div>


            <div style={{ overflowY: "auto", flex: 1 }}>
              {/* To row */}
              <div style={{ display: "flex", alignItems: "center", padding: "10px 20px", borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", width: 32, flexShrink: 0 }}>To</span>
                <span style={{ flex: 1, fontSize: 13, color: "#1a2332" }}>{selectedLead?.email}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setShowCc((v) => !v)} style={{
                    padding: "3px 10px", borderRadius: 4, border: "1px solid #e2e8f0",
                    background: showCc ? "#ede9fe" : "#fff", color: showCc ? "#7c3aed" : "#64748b",
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}>Cc</button>
                  <button onClick={() => setShowBcc((v) => !v)} style={{
                    padding: "3px 10px", borderRadius: 4, border: "1px solid #e2e8f0",
                    background: showBcc ? "#ede9fe" : "#fff", color: showBcc ? "#7c3aed" : "#64748b",
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}>Bcc</button>
                </div>
              </div>

              {/* Cc row */}
              {showCc && (
                <div style={{ display: "flex", alignItems: "center", padding: "8px 20px", borderBottom: "1px solid #f0f0f0" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", width: 32, flexShrink: 0 }}>Cc</span>
                  <input value={composeCc} onChange={(e) => setComposeCc(e.target.value)}
                    placeholder="Cc recipients..." style={{ flex: 1, border: "none", outline: "none", fontSize: 13, color: "#1a2332" }} />
                </div>
              )}

              {/* Bcc row */}
              {showBcc && (
                <div style={{ display: "flex", alignItems: "center", padding: "8px 20px", borderBottom: "1px solid #f0f0f0" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", width: 32, flexShrink: 0 }}>Bcc</span>
                  <input value={composeBcc} onChange={(e) => setComposeBcc(e.target.value)}
                    placeholder="Bcc recipients..." style={{ flex: 1, border: "none", outline: "none", fontSize: 13, color: "#1a2332" }} />
                </div>
              )}

              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Email Template */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>EMAIL TEMPLATE</span>
                    {selectedTemplateId && (
                      <button
                        onClick={() => {
                          setSelectedTemplateId("");
                          setSelectedTemplate(null);
                          setComposeSubject("");
                          setBodyHtml("");
                          setBodyMode("compose");
                        }}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          fontSize: 11, color: "#ef4444", fontWeight: 600,
                          display: "flex", alignItems: "center", gap: 3, padding: 0,
                        }}
                      >
                        <i className="bx bx-x" style={{ fontSize: 14 }} /> Clear
                      </button>
                    )}
                  </div>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedTemplateId(id);
                      const tmpl = emailTemplates.find((t) => String(t.id) === id) || null;
                      setSelectedTemplate(tmpl);
                      if (tmpl) {
                        setComposeSubject(tmpl.subject || "");
                        setBodyHtml(tmpl.body || "");
                        setBodyMode("preview");
                        fetchPreview(selectedLead?.id, tmpl.subject || "", tmpl.body || "");
                      } else {
                        setComposeSubject("");
                        setBodyHtml("");
                        setBodyMode("compose");
                      }
                    }}
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 6,
                      border: "1px solid #e2e8f0", fontSize: 13, color: "#1a2332",
                      background: "#fff", outline: "none", cursor: "pointer",
                      appearance: "auto",
                    }}
                  >
                    <option value="">— No Template (Manual) —</option>
                    {emailTemplates.map((t) => (
                      <option key={t.id} value={String(t.id)}>{t.template_name}</option>
                    ))}
                  </select>
                </div>

                {/* Subject */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", marginBottom: 6 }}>SUBJECT</div>
                  <input
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Subject..."
                    readOnly={!!selectedTemplateId}
                    style={{
                      width: "100%", boxSizing: "border-box", padding: "9px 12px",
                      border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13,
                      color: "#1a2332", outline: "none", background: selectedTemplateId ? "#f8fafc" : "#fff",
                    }}
                  />
                </div>

                {/* Body — a design template: just the message, the design around it */}
                {templateWidget ? (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", marginBottom: 6 }}>MESSAGE</div>
                    <WidgetBodyEditor widget={templateWidget} value={bodyHtml} onChange={setBodyHtml} subject={composeSubject} greeting={selectedTemplate?.widget_greeting ?? undefined} showPreview minHeight={160} />
                  </div>
                ) : (
                /* Body — 3-mode: Compose | HTML | Preview */
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      BODY
                      {previewLoading && (
                        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#7c3aed", fontWeight: 500 }}>
                          <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} />
                          Resolving…
                        </span>
                      )}
                    </span>
                    {/* Mode tabs */}
                    <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 6, padding: 2 }}>
                      {[
                        { mode: "compose", icon: "bx-edit", label: "Compose" },
                        { mode: "html",    icon: "bx-code",  label: "HTML"    },
                        { mode: "preview", icon: "bx-show",  label: "Preview" },
                      ].map(({ mode, icon, label }) => (
                        <button
                          key={mode}
                          onClick={() => setBodyMode(mode)}
                          style={{
                            padding: "3px 10px", borderRadius: 4, border: "none",
                            background: bodyMode === mode ? "#fff" : "transparent",
                            color: bodyMode === mode ? "#7c3aed" : "#64748b",
                            fontSize: 11, fontWeight: 600, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 4,
                            boxShadow: bodyMode === mode ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                          }}
                        >
                          <i className={`bx ${icon}`} style={{ fontSize: 13 }} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                    {bodyMode === "compose" && (
                      <>
                        {/* Formatting toolbar */}
                        <div style={{
                          display: "flex", alignItems: "center", gap: 2, padding: "6px 10px",
                          borderBottom: "1px solid #f0f0f0", background: "#fafafa", flexWrap: "wrap",
                        }}>
                          {[
                            { icon: "bx-bold",      cmd: "bold",                title: "Bold"           },
                            { icon: "bx-italic",    cmd: "italic",              title: "Italic"         },
                            { icon: "bx-underline", cmd: "underline",           title: "Underline"      },
                          ].map(({ icon, cmd, title }) => (
                            <button key={cmd} title={title}
                              onMouseDown={(e) => { e.preventDefault(); execCmd(cmd); }}
                              style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
                              <i className={`bx ${icon}`} />
                            </button>
                          ))}
                          <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                          {[
                            { icon: "bx-list-ul", cmd: "insertUnorderedList", title: "Bullet List"    },
                            { icon: "bx-list-ol", cmd: "insertOrderedList",   title: "Numbered List"  },
                          ].map(({ icon, cmd, title }) => (
                            <button key={cmd} title={title}
                              onMouseDown={(e) => { e.preventDefault(); execCmd(cmd); }}
                              style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                              <i className={`bx ${icon}`} />
                            </button>
                          ))}
                          <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                          {[
                            { icon: "bx-align-left",   cmd: "justifyLeft",   title: "Align Left"   },
                            { icon: "bx-align-middle", cmd: "justifyCenter", title: "Align Center" },
                            { icon: "bx-align-right",  cmd: "justifyRight",  title: "Align Right"  },
                          ].map(({ icon, cmd, title }) => (
                            <button key={cmd} title={title}
                              onMouseDown={(e) => { e.preventDefault(); execCmd(cmd); }}
                              style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                              <i className={`bx ${icon}`} />
                            </button>
                          ))}
                          <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                          <button title="Link"
                            onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL"); if (url) execCmd("createLink", url); }}
                            style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                            <i className="bx bx-link" />
                          </button>
                          <button title="Undo"
                            onMouseDown={(e) => { e.preventDefault(); execCmd("undo"); }}
                            style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                            <i className="bx bx-undo" />
                          </button>
                          <button title="Redo"
                            onMouseDown={(e) => { e.preventDefault(); execCmd("redo"); }}
                            style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                            <i className="bx bx-redo" />
                          </button>
                        </div>
                        {/* contentEditable — syncs to bodyHtml state on every keystroke */}
                        <div
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning
                          onInput={() => setBodyHtml(editorRef.current?.innerHTML || "")}
                          style={{
                            minHeight: 140, padding: "12px 14px", fontSize: 13,
                            color: "#1a2332", outline: "none", lineHeight: 1.7,
                            wordBreak: "break-word",
                          }}
                        />
                      </>
                    )}

                    {bodyMode === "html" && (
                      <textarea
                        value={bodyHtml}
                        onChange={(e) => setBodyHtml(e.target.value)}
                        rows={10}
                        placeholder="Paste or write your HTML here…"
                        style={{
                          width: "100%", boxSizing: "border-box", padding: "12px",
                          border: "none", outline: "none", resize: "vertical",
                          fontSize: 12, fontFamily: "monospace", color: "#1a2332", lineHeight: 1.6,
                          background: "#fafafa",
                        }}
                      />
                    )}

                    {bodyMode === "preview" && (
                      previewLoading ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "48px 0" }}>
                          <span className="spinner-border spinner-border-sm" style={{ color: "#7c3aed", width: 22, height: 22 }} />
                        </div>
                      ) : bodyHtml ? (
                        /* Scrollable, isolated iframe — zero style leakage */
                        <div style={{ maxHeight: 320, overflowY: "auto" }}>
                          <iframe
                            srcDoc={bodyHtml}
                            style={{ width: "100%", border: "none", display: "block", minHeight: 220 }}
                            onLoad={(e) => {
                              const doc = e.target.contentDocument;
                              if (doc) e.target.style.height = doc.body.scrollHeight + 16 + "px";
                            }}
                            title="email-preview"
                            sandbox="allow-same-origin"
                          />
                        </div>
                      ) : (
                        <div style={{
                          minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#94a3b8", fontSize: 13, flexDirection: "column", gap: 6,
                        }}>
                          <i className="bx bx-envelope" style={{ fontSize: 28, color: "#cbd5e1" }} />
                          <span>Nothing to preview yet — compose a message or select a template</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
                )}

                {/* Attachments chips */}
                {composeAttachments.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {composeAttachments.map((f, i) => (
                      <span key={i} style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "3px 10px", borderRadius: 20, background: "#ede9fe",
                        border: "1px solid #ddd6fe", fontSize: 12, color: "#7c3aed",
                      }}>
                        <i className="bx bx-paperclip" style={{ fontSize: 12 }} />
                        {f.name}
                        <span onClick={() => setComposeAttachments((p) => p.filter((_, j) => j !== i))}
                          style={{ cursor: "pointer", color: "#94a3b8", fontWeight: 700, marginLeft: 2 }}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 20px", borderTop: "1px solid #f0f0f0", flexShrink: 0, background: "#fff",
            }}>
              <button onClick={() => fileInputRef.current?.click()} style={{
                padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0",
                background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              }}>
                <i className="bx bx-paperclip" style={{ fontSize: 15 }} /> Attach Files
              </button>
              <input ref={fileInputRef} type="file" multiple style={{ display: "none" }}
                onChange={(e) => setComposeAttachments((p) => [...p, ...Array.from(e.target.files || [])])} />

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={closeCompose} style={{
                  padding: "8px 20px", borderRadius: 8, border: "1px solid #e2e8f0",
                  background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}>Cancel</button>
                <button onClick={handleSend} disabled={sending} style={{
                  padding: "8px 22px", borderRadius: 8, border: "none",
                  background: sending ? "#a78bfa" : "linear-gradient(135deg,#5b48e8,#7c3aed)",
                  color: "#fff", fontSize: 13, fontWeight: 600, cursor: sending ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 7,
                  boxShadow: sending ? "none" : "0 2px 10px rgba(91,72,232,0.35)",
                }}>
                  {sending
                    ? <><span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> Sending...</>
                    : <><i className="bx bx-send" style={{ fontSize: 15 }} /> Send</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
